/**
 * Group by, for lists. The server pages the list, so grouping cannot gather a
 * group's rows from every page. Instead the list is sorted by the group column
 * first (groupFirstSort), and a header row is drawn wherever the value changes
 * (groupBoundaries). Headers show the value but no count: a count could only
 * cover the rows loaded so far, and that number would be wrong.
 */

import { idLookupDisplayString } from '@/constants/lookupFilterOptions';
import type { SortDirection, TableColumnStaticProps } from '@/types/tableTypes';

function readPath(row: Record<string, unknown>, path: string): unknown {
    return path.split('.').reduce<unknown>(
        (value, part) => (value !== null && typeof value === 'object' ? (value as Record<string, unknown>)[part] : undefined),
        row,
    );
}

function valueLabel(value: unknown): string {
    if (value === null || value === undefined || value === '') {
        return '(empty)';
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    if (typeof value === 'object') {
        const record = value as Record<string, unknown>;
        const name = record.display_string ?? record.name;
        return typeof name === 'string' && name !== '' ? name : '(empty)';
    }
    return String(value);
}

/** Row index → header label, for each row that starts a new group. */
export function groupBoundaries(
    rows: Record<string, unknown>[],
    field: string | null,
    /** Names a raw value, such as brand_id 2 → "TPG". Null falls back to the value itself. */
    nameFor: (value: unknown) => string | null = () => null,
): Map<number, string> {
    const boundaries = new Map<number, string>();
    if (!field) {
        return boundaries;
    }
    let previous: string | undefined;
    rows.forEach((row, index) => {
        const value = readPath(row, field);
        const label = nameFor(value) ?? valueLabel(value);
        if (label !== previous) {
            boundaries.set(index, label);
            previous = label;
        }
    });
    return boundaries;
}

/**
 * The group headers for a list, the same for the table and the cards. It finds the
 * group column among `columns`, so a lookup id is named the way the table cell names
 * it, such as account_status_id 1 → "Active".
 */
export function groupHeadersFor(
    rows: Record<string, unknown>[],
    groupByField: string | null,
    columns: Pick<TableColumnStaticProps, 'key' | 'field' | 'filterControl'>[],
): Map<number, string> {
    const groupColumn = columns.find((column) => column.key === groupByField || column.field === groupByField);
    return groupBoundaries(rows, groupColumn?.key ?? groupByField, (value) =>
        idLookupDisplayString(groupColumn?.filterControl?.optionsSource, value),
    );
}

/**
 * The sort the server gets while grouping: the group column first, then the
 * user's own sort. Object key order is sort priority in this app.
 */
export function groupFirstSort<T extends Record<string, SortDirection>>(sort: T, groupField: string | null): T {
    if (!groupField) {
        return sort;
    }
    const { [groupField]: groupDirection, ...rest } = sort;
    return { [groupField]: groupDirection ?? 'asc', ...rest } as unknown as T;
}
